import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, CardHeader, Pill, EmptyState, Skeleton } from "../../Components/ui";

const inputStyle = { border: "1px solid var(--border-default)", borderRadius: 8, padding: "6px 10px", fontSize: 13, background: "var(--bg-surface)", color: "var(--text-primary)", width: "100%" };
const btnPrimary = { background: "var(--accent-primary)", color: "white", padding: "8px 16px", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" };
const btnGhost = { background: "transparent", color: "var(--text-secondary)", padding: "6px 12px", border: "1px solid var(--border-default)", borderRadius: 8, fontSize: 13, cursor: "pointer" };

export default function NoticesPage() {
  const toast = useToast();
  const isAdmin = localStorage.getItem("role") === "SUPER_ADMIN";
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", body: "", audience: "all", pinned: false });
  const [showForm, setShowForm] = useState(false);

  useShellHeader({ title: "Notices", breadcrumb: "Org announcements" }, []);

  async function load() {
    setLoading(true);
    try { const d = await api("/notices"); setNotices(d.notices || []); }
    catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function markRead(id) {
    try { await api(`/notices/${id}/read`, { method: "POST" }); load(); }
    catch (e) { /* silent */ }
  }
  async function create() {
    if (!form.title) { toast({ title: "Title required", status: "warning" }); return; }
    try {
      await api("/notices", { method: "POST", body: JSON.stringify(form) });
      toast({ title: "Notice posted", status: "success" });
      setForm({ title: "", body: "", audience: "all", pinned: false });
      setShowForm(false);
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }
  async function remove(id) {
    if (!window.confirm("Delete this notice?")) return;
    try { await api(`/notices/${id}`, { method: "DELETE" }); load(); }
    catch (e) { toast({ title: e.message, status: "error" }); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {isAdmin && (
        <Card padding="none">
          <CardHeader title="Post notice" action={
            <button style={btnPrimary} onClick={() => setShowForm((v) => !v)}>{showForm ? "Cancel" : "+ New notice"}</button>
          } />
          {showForm && (
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <input style={inputStyle} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <textarea style={{ ...inputStyle, minHeight: 120, resize: "vertical" }} placeholder="Body (HTML allowed, sanitized)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <select style={{ ...inputStyle, width: 180 }} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                  <option value="all">Everyone</option>
                  <option value="team">Specific team(s)</option>
                  <option value="specific">Specific people</option>
                </select>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-secondary)" }}>
                  <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> Pin
                </label>
                <div style={{ flex: 1 }} />
                <button style={btnPrimary} onClick={create}>Post</button>
              </div>
            </div>
          )}
        </Card>
      )}

      {loading ? <Skeleton.Card /> : notices.length === 0 ? (
        <Card padding="lg"><EmptyState icon="📢" title="No notices" description="Nothing has been posted yet." /></Card>
      ) : (
        notices.map((n) => (
          <Card key={n._id} padding="lg" interactive onClick={() => !n.isRead && markRead(n._id)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                  {n.pinned && <Pill tone="warning" size="sm">📌 pinned</Pill>}
                  {n.audience !== "all" && <Pill tone="info" size="sm">{n.audience}</Pill>}
                  {!n.isRead && <Pill tone="brand" size="sm">new</Pill>}
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>{n.title}</div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                  {n.createdBy?.username || "—"} · {new Date(n.createdAt).toLocaleString()}
                  {n.readCount > 0 && ` · ${n.readCount} read`}
                </div>
                <div style={{ marginTop: 10, fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: n.body }} />
              </div>
              {isAdmin && (
                <button style={btnGhost} onClick={(e) => { e.stopPropagation(); remove(n._id); }}>Delete</button>
              )}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
